import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'
import type { SelectOption } from '@/types'

import { ChevronDown, CircleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatMobile } from '@/lib/validation'

interface FieldProps {
    id: string
    label: string
    required?: boolean
    optional?: boolean
    hint?: string
    error?: string
    className?: string
    children: ReactNode
}

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
    invalid?: boolean
    icon?: ReactNode
    trailing?: ReactNode
}

interface SelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
    invalid?: boolean
    options: Array<SelectOption>
    placeholder: string
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    invalid?: boolean
}

interface MobileInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
    value: string
    onValueChange: (value: string) => void
    invalid?: boolean
}

const controlBase = 'w-full rounded-lg border border-input bg-white text-[15px] text-foreground shadow-xs transition-[border-color,box-shadow] placeholder:text-muted-foreground/70 hover:border-primary/40 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/15 disabled:cursor-not-allowed disabled:opacity-60'

export const fieldDescription = (id: string, error?: string, hint?: string): string | undefined => {
    if (error) return `${id}-error`
    if (hint) return `${id}-hint`

    return undefined
}

export const Field: FC<FieldProps> = ({
    id,
    label,
    required,
    optional,
    hint,
    error,
    className,
    children
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <label htmlFor={id} className='flex items-baseline justify-between gap-2 text-sm font-medium text-foreground'>
                <span>
                    {label}
                    {required && <span className='ml-0.5 text-primary' aria-hidden='true'>*</span>}
                </span>

                {optional && <span className='text-xs font-normal text-muted-foreground'>Optional</span>}
            </label>

            {children}

            {error ? (
                <p id={`${id}-error`} className='flex items-start gap-1.5 text-xs font-medium text-destructive'>
                    <CircleAlert className='mt-px size-3.5 shrink-0' aria-hidden='true' />
                    {error}
                </p>
            ) : hint ? (
                <p id={`${id}-hint`} className='text-xs text-muted-foreground'>
                    {hint}
                </p>
            ) : null}
        </div>
    )
}

export const TextInput: FC<TextInputProps> = ({ invalid, icon, trailing, className, ...props }): ReactNode => {
    return (
        <div className='relative'>
            {icon && (
                <span className='pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-muted-foreground'>
                    {icon}
                </span>
            )}

            <input
                aria-invalid={invalid || undefined}
                className={cn(controlBase, 'h-11 px-3.5', icon && 'pl-10', trailing && 'pr-12', className)}
                {...props}
            />

            {trailing && (
                <span className='absolute inset-y-0 right-1 flex items-center'>
                    {trailing}
                </span>
            )}
        </div>
    )
}

export const SelectInput: FC<SelectInputProps> = ({ invalid, options, placeholder, className, value, ...props }): ReactNode => {
    return (
        <div className='relative'>
            <select
                value={value}
                aria-invalid={invalid || undefined}
                className={cn(controlBase, 'h-11 cursor-pointer appearance-none pl-3.5 pr-10 [&>option]:text-foreground', value === '' && 'text-muted-foreground', className)}
                {...props}
            >
                <option value='' disabled>
                    {placeholder}
                </option>

                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>

            <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
        </div>
    )
}

export const TextArea: FC<TextAreaProps> = ({ invalid, className, ...props }): ReactNode => {
    return (
        <textarea
            aria-invalid={invalid || undefined}
            className={cn(controlBase, 'min-h-28 resize-y px-3.5 py-3 leading-relaxed', className)}
            {...props}
        />
    )
}

export const MobileInput: FC<MobileInputProps> = ({ value, onValueChange, invalid, className, ...props }): ReactNode => {
    return (
        <div
            className={cn(
                'flex h-11 w-full overflow-hidden rounded-lg border border-input bg-white shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/15',
                invalid && 'border-destructive hover:border-destructive focus-within:border-destructive focus-within:ring-destructive/15',
                className
            )}
        >
            <span className='flex select-none items-center border-r border-input bg-muted px-3 text-sm font-medium text-muted-foreground' aria-hidden='true'>
                +91
            </span>

            <input
                type='tel'
                inputMode='numeric'
                autoComplete='tel-national'
                value={value}
                onChange={(event) => onValueChange(formatMobile(event.target.value))}
                aria-invalid={invalid || undefined}
                className='h-full w-full min-w-0 bg-transparent px-3 text-[15px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none'
                {...props}
            />
        </div>
    )
}
